// src/views/PagosAprobacionView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { listPagos } from "../../services/pagosService";
import { ModalPagoDetalle } from "../../modals/ModalPagoDetalle";
import { ModalCrearOrdenPago } from "../../modals/ModalCrearOrdenPago";

/* ===== Helpers ===== */

function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function saveLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, saved_at: Date.now() }));
  } catch {}
}

function readLS(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw).value : null;
  } catch {
    return null;
  }
}

const LS_PAGOS = "pagos_aprobacion_cache";

const PLACEHOLDER_RECEIPT =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='160' viewBox='0 0 220 160'%3E%3Crect width='220' height='160' rx='16' fill='%23f8fafc'/%3E%3Crect x='32' y='26' width='156' height='108' rx='10' fill='%23e5e7eb'/%3E%3Cpath d='M52 54h116M52 74h80M52 94h60' stroke='%239ca3af' stroke-width='4' stroke-linecap='round'/%3E%3Ccircle cx='158' cy='100' r='12' fill='%23ffffff' stroke='%236b7280' stroke-width='2'/%3E%3Cpath d='M152 100l4 4 8-8' stroke='%2365a30d' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E";

/**
 * Procesa la URL del comprobante de pago.
 * Ahora el backend retorna URLs directas de Supabase Storage.
 * Esta función se usa para las tarjetas/lista (no para el modal).
 * @param {string|null} comprobanteUrl - URL del comprobante
 * @returns {string} URL del comprobante o placeholder
 */
function processComprobanteImage(comprobanteUrl) {
  if (!comprobanteUrl || typeof comprobanteUrl !== "string") {
    return PLACEHOLDER_RECEIPT;
  }

  const trimmed = comprobanteUrl.trim();
  if (!trimmed) {
    return PLACEHOLDER_RECEIPT;
  }

  // Si es una URL válida (HTTP/HTTPS), retornarla directamente
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Si por alguna razón sigue siendo base64 (data URL), manejarlo
  if (trimmed.startsWith("data:image/")) {
    return trimmed;
  }

  // En cualquier otro caso, usar placeholder
  console.warn("Formato de comprobante no reconocido:", trimmed.substring(0, 50));
  return PLACEHOLDER_RECEIPT;
}

/**
 * Determina si se debe mostrar el badge de revisión pendiente
 * con la nueva lógica basada en estado = "sended".
 * @param {Object} pago - Objeto del pago con los campos del backend
 * @returns {boolean} true si debe mostrar el badge
 */
function shouldShowPendingBadge(pago) {
  // Nueva lógica:
  // Se quiere resaltar los pagos que ya fueron ENVIADOS (estado backend "sended"),
  // es decir, que están listos para revisión por parte del admin.
  const estado = (pago.estado || "").toString().toLowerCase();
  return estado === "sended";
}

/**
 * Formatea monto a USD (ajusta si usas otra moneda)
 */
function formatMoney(value) {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "—";
  }
  try {
    return new Intl.NumberFormat("es-EC", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return `${Number(value).toFixed(2)} USD`;
  }
}

/**
 * Formatea fechas (YYYY-MM-DD o ISO) a formato legible
 */
function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("es-EC", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/* ===== Estados (mismos textos que la primera view) ===== */

const STATUS_VARIANTS = {
  Pendiente: "warning",
  Enviado: "info",
  Aprobado: "success",
  Rechazado: "danger",
};

/* ===== Componentes UI simples ===== */

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
  };

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant] || variants.default
      )}
    >
      {children}
    </span>
  );
}

// Estados que vienen del backend → etiquetas de UI
const ESTADO_API_TO_UI = {
  // valores del backend
  pending: "Pendiente",
  paid: "Aprobado",
  overdue: "Pendiente",
  cancelled: "Rechazado",
  sended: "Enviado", // 👈 nuevo estado del backend

  // por si en algún momento ya vienen “bonitos” en español
  Pendiente: "Pendiente",
  Enviado: "Enviado",
  Aprobado: "Aprobado",
  Rechazado: "Rechazado",
};

// Tipo de pago que viene del backend → etiqueta de UI
const TIPO_PAGO_API_TO_UI = {
  efectivo: "Efectivo",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
  por_definir: "Por definir",
};

/* ===== Normalización de pago usando deportistas / representantes ===== */

function normalizePago(pago, atletasMap, repsMap) {
  const atleta = atletasMap[pago.deportista_id] || {};
  const rep = repsMap[pago.representante_id] || {};

  const deportistaNombre =
    `${atleta.nombres || ""} ${atleta.apellidos || ""}`.trim() ||
    "Sin deportista";
  const deportistaCedula = atleta.cedula || "";
  
  const representanteNombre =
    `${rep.nombres || ""} ${rep.apellidos || ""}`.trim() || "Sin representante";
  const representanteCedula = rep.cedula || "";

  // 🔹 Colaborador que confirmó el pago
  const colaborador = pago.colaboradores || {};
  const colaboradorNombre =
    `${colaborador.nombres || ""} ${colaborador.apellidos || ""}`.trim() || null;
  const fechaConfirmacion = pago.fecha_confirmacion || null;
  const confirmadoPor = pago.confirmado_por || colaborador.id || null;

  // 🔹 Estado: mapear del backend a etiqueta de UI
  const estadoApi = pago.estado || "pending";
  const estadoUi = ESTADO_API_TO_UI[estadoApi] || "Pendiente";

  // 🔹 Tipo de pago: mapear para mostrar bonito
  const tipoPagoApi = pago.tipo_pago || "por_definir";
  const tipoPagoUi = TIPO_PAGO_API_TO_UI[tipoPagoApi] || "Por definir";

  // 🔹 Comprobante: usar URL cruda (para modal) y procesada (para tarjetas)
  const comprobanteRaw = pago.comprobante_url || "";
  const comprobanteUrlProcessed = processComprobanteImage(comprobanteRaw);

  // 🔹 Producto: usar el objeto productos si existe
  const productoNombre = pago.productos?.nombre || pago.producto || "Pago";
  const productoId = pago.producto_id || null;
  const productoMonto = pago.productos?.monto ?? pago.monto ?? 0;

  // 🔹 Verificar si debe mostrar badge de revisión pendiente (estado sended)
  const mostrarBadgeRevision = shouldShowPendingBadge(pago);

  // 🔹 Solo se puede aprobar/rechazar si está Enviado
  const editable = estadoUi === "Enviado";

  return {
    id: pago.id,
    producto: productoNombre,
    productoId: productoId,
    monto: productoMonto,
    periodo: pago.periodo || "—",
    fechaVencimiento: pago.fecha_vencimiento || "",
    estado: estadoUi,
    tipoPago: tipoPagoUi,
    creadoEn: pago.creado_en || pago.created_at || "",
    actualizadoEn: pago.actualizado_en || pago.updated_at || "",
    representanteNombre,
    representanteCedula,
    deportistaNombre,
    deportistaCedula,
    representanteTelefono: rep.telefono || rep.celular || "",
    representanteEmail: rep.email || "",
    // info del colaborador
    colaboradorNombre,
    fechaConfirmacion,
    confirmadoPor,
    // info para el modal
    representanteId: pago.representante_id || null,
    deportistaId: pago.deportista_id || null,
    comentario: pago.comentario || "",
    comprobante: comprobanteRaw,          // crudo para modal
    comprobanteUrl: comprobanteUrlProcessed, // procesado para tarjetas/lista
    // badge de revisión pendiente
    mostrarBadgeRevision,
    // control para aprobar/rechazar
    editable,
  };
}

/**
 * Adapta el pago normalizado al formato que espera ModalPagoDetalle
 */
function mapPagoToModal(p) {
  if (!p) return null;

  // Mostrar comprobante si existe (sin importar el estado, ya que puede estar aprobado/rechazado)
  const hasRealComprobante =
    p.comprobante &&
    typeof p.comprobante === "string" &&
    p.comprobante.trim() !== "" &&
    !p.comprobante.includes("placeholder") &&
    !p.comprobante.includes("data:image/svg+xml");

  return {
    id: p.id,
    producto: p.producto,
    producto_id: p.productoId,
    productos: {
      nombre: p.producto,
      monto: p.monto,
    },
    monto: p.monto,
    periodo: p.periodo,
    fecha_vencimiento: p.fechaVencimiento,
    estado: p.estado,
    tipo_pago: p.tipoPago,
    comentario: p.comentario,
    // Información del representante (objeto anidado)
    representantes: {
      nombres: p.representanteNombre?.split(" ")[0] || "",
      apellidos: p.representanteNombre?.split(" ").slice(1).join(" ") || "",
      cedula: p.representanteCedula || "",
      email: p.representanteEmail || "",
      telefono: p.representanteTelefono || "",
    },
    representante_nombre: p.representanteNombre,
    representante_id: p.representanteId,
    // Información del deportista (objeto anidado)
    deportistas: {
      nombres: p.deportistaNombre?.split(" ")[0] || "",
      apellidos: p.deportistaNombre?.split(" ").slice(1).join(" ") || "",
      cedula: p.deportistaCedula || "",
    },
    deportista_nombre: p.deportistaNombre,
    deportista_id: p.deportistaId,
    // Información del colaborador (objeto anidado)
    colaboradores: {
      nombres: p.colaboradorNombre?.split(" ")[0] || "",
      apellidos: p.colaboradorNombre?.split(" ").slice(1).join(" ") || "",
      id: p.confirmadoPor || null,
    },
    confirmado_por: p.confirmadoPor,
    fecha_confirmacion: p.fechaConfirmacion,
    created_at: p.creadoEn,
    // URL del comprobante (pasar la URL real del backend)
    comprobante_url: hasRealComprobante ? p.comprobante : null,
    // Control de edición
    editable: !!p.editable,
  };
}

/* ===== View principal ===== */

export default function PagosAprobacionView() {
  const [pagos, setPagos] = useState([]);
  const [atletas, setAtletas] = useState([]); // lista completa de deportistas
  const [repsById, setRepsById] = useState({}); // mapa id → representante
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos"); // "todos" | pendiente | enviado | aprobado | rechazado
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Pago seleccionado para el modal de detalle
  const [selectedPago, setSelectedPago] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Modal para crear orden
  const [createOpen, setCreateOpen] = useState(false);

  async function loadPagos() {
    try {
      setLoading(true);
      setError("");

      const pagosData = await listPagos();

      // Construir mapa de deportistas a partir de los pagos (campo `deportistas`)
      const atletasMap = {};
      (pagosData || []).forEach((p) => {
        const d = p.deportistas || p.deportista || null;
        if (d && d.id) atletasMap[d.id] = d;
      });

      // Construir mapa de representantes a partir de los pagos (campo `representantes`)
      const repsMap = {};
      (pagosData || []).forEach((p) => {
        const r = p.representantes || p.representante || null;
        if (r && r.id) repsMap[r.id] = r;
      });

      const normalized = (pagosData || []).map((p) =>
        normalizePago(p, atletasMap, repsMap)
      );

      // Convertir atletasMap a array para mantener compatibilidad con ModalCrearOrdenPago
      const atletasArray = Object.values(atletasMap);

      setPagos(normalized);
      setAtletas(atletasArray);
      setRepsById(repsMap);
      saveLS(LS_PAGOS, normalized);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error cargando pagos");
      toast.error(e?.message || "Error cargando pagos para aprobación");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const cached = readLS(LS_PAGOS);
    if (cached) {
      setPagos(cached);
      setLoading(false);
    }

    let mounted = true;
    (async () => {
      try {
        await loadPagos();
      } catch {
        if (mounted) {
          // error ya manejado en loadPagos
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = (query || "").toString().toLowerCase().trim();
    return pagos.filter((p) => {
      // Construir un haystack más completo con todos los campos buscables
      const haystack = `${p.representanteNombre} ${p.representanteCedula || ""} ${p.representanteEmail || ""} ${p.representanteTelefono || ""} ${p.deportistaNombre} ${p.deportistaCedula || ""} ${p.colaboradorNombre || ""} ${
        p.producto || ""
      } ${p.periodo || ""}`.toLowerCase();
      const matches = haystack.includes(term);

      let statusOk = true;
      if (statusFilter === "pendiente") statusOk = p.estado === "Pendiente";
      if (statusFilter === "enviado") statusOk = p.estado === "Enviado";
      if (statusFilter === "aprobado") statusOk = p.estado === "Aprobado";
      if (statusFilter === "rechazado") statusOk = p.estado === "Rechazado";

      // Filtro por rango de fechas (campo creado_en)
      let dateOk = true;
      if (dateFrom || dateTo) {
        const creadoEn = p.creadoEn ? new Date(p.creadoEn) : null;
        if (creadoEn) {
          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (creadoEn < fromDate) dateOk = false;
          }
          if (dateTo) {
            const toDate = new Date(dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (creadoEn > toDate) dateOk = false;
          }
        } else {
          dateOk = false;
        }
      }

      return matches && statusOk && dateOk;
    });
  }, [pagos, query, statusFilter, dateFrom, dateTo]);

  function openDetalle(pago) {
    setSelectedPago(pago);
    setModalOpen(true);
  }

  function closeDetalle() {
    setModalOpen(false);
    setSelectedPago(null);
  }

  function openCreateModal() {
    setCreateOpen(true);
  }

  function closeCreateModal() {
    setCreateOpen(false);
  }

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">
            Pagos — Revisión y aprobación
          </h1>
          <p className="text-slate-600 text-sm">
            Revisa los pagos enviados por los representantes. Haz clic en un
            registro para ver el detalle y aprobar o rechazar cuando corresponda.
            También puedes generar nuevas órdenes de pago.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
        {/* Buscador */}
        <div className="flex-1">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar por representante, deportista, colaborador, cédula, email o producto"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Filtro por rango de fechas */}
        <div className="flex items-center gap-2">
          <DatePicker
            placeholder="Desde"
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(date) => {
              const newDateFrom = date ? date.toDate() : null;
              // Validar que fecha desde no sea mayor que fecha hasta
              if (newDateFrom && dateTo && newDateFrom > dateTo) {
                toast.warning("La fecha 'Desde' no puede ser mayor que la fecha 'Hasta'");
                return;
              }
              setDateFrom(newDateFrom);
            }}
            format="DD/MM/YYYY"
            className="rounded-lg"
            style={{ width: 130 }}
          />
          <span className="text-slate-400">—</span>
          <DatePicker
            placeholder="Hasta"
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(date) => {
              const newDateTo = date ? date.toDate() : null;
              // Validar que fecha hasta no sea menor que fecha desde
              if (newDateTo && dateFrom && newDateTo < dateFrom) {
                toast.warning("La fecha 'Hasta' no puede ser menor que la fecha 'Desde'");
                return;
              }
              setDateTo(newDateTo);
            }}
            format="DD/MM/YYYY"
            className="rounded-lg"
            style={{ width: 130 }}
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom(null);
                setDateTo(null);
              }}
              className="text-xs text-slate-600 hover:text-blue-600 underline whitespace-nowrap"
              title="Limpiar filtro de fechas"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Filtro por estado */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 whitespace-nowrap">Estado:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="enviado">Enviado</option>
            <option value="aprobado">Aprobado</option>
            <option value="rechazado">Rechazado</option>
          </select>
        </div>

        {/* Switch vista */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tabla"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13"
              />
            </svg>
          </button>
        </div>

        {/* Botón Recargar */}
        <button
          type="button"
          onClick={loadPagos}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>

        {/* Botón Nuevo Pago */}
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium shadow-sm hover:bg-blue-700 hover:shadow-md transition whitespace-nowrap"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Nuevo Pago
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-40 rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <div className="animate-pulse h-full bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {/* Cards View (resumidos) */}
      {!loading && viewMode === "cards" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <svg
                  className="w-8 h-8 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0V4m0 8v4m-7 4h14a2 2 0 002-2v-2.5a4.5 4.5 0 00-2.695-4.12L14 9.5m-4 0L5.695 11.38A4.5 4.5 0 003 15.5V18a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                No hay pagos para mostrar
              </h3>
              <p className="text-sm text-slate-500">
                {query
                  ? "Intenta ajustar los filtros o la búsqueda."
                  : "Cuando se registren pagos aparecerán aquí para revisión."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((pago) => {
                const statusVariant = STATUS_VARIANTS[pago.estado] || "default";

                return (
                  <article
                    key={pago.id}
                    className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md hover:border-blue-200 transition cursor-pointer relative"
                    onClick={() => openDetalle(pago)}
                  >
                    {pago.mostrarBadgeRevision && (
                      <div className="absolute top-2 left-2 z-10">
                        <div
                          className="w-3 h-3 rounded-full bg-red-500 shadow-md"
                          title="Requiere revisión"
                        />
                      </div>
                    )}
                    <div className="p-4 flex flex-col gap-3">
                      {/* Encabezado compacto */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold truncate">
                            {pago.producto}
                          </h3>
                          <p className="text-[11px] text-slate-500 truncate">
                            {pago.periodo !== "—"
                              ? `Periodo ${pago.periodo}`
                              : "Sin periodo"}
                          </p>
                        </div>
                        <Badge variant={statusVariant}>{pago.estado}</Badge>
                      </div>

                      {/* Info representante */}
                      <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-blue-600 uppercase tracking-wide">Representante</p>
                            <p className="text-xs font-semibold text-slate-900 truncate">{pago.representanteNombre}</p>
                            {pago.representanteCedula && (
                              <p className="text-[11px] text-slate-600">CI: {pago.representanteCedula}</p>
                            )}
                            {pago.representanteEmail && (
                              <p className="text-[11px] text-slate-600">Email: {pago.representanteEmail}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Info deportista */}
                      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-.03-.29-.06-.58-.12-.86l-.93.93c-.3.3-.77.3-1.07 0l-.93-.93c-.06.28-.09.57-.12.86V20h3v-.07zM8 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm6 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-2-6.93V4h3v.07c-.03.29-.06.58-.12.86l-.93-.93c-.3-.3-.77-.3-1.07 0l-.93.93c.06-.28.09-.57.12-.86z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wide">Deportista</p>
                            <p className="text-xs font-semibold text-slate-900">{pago.deportistaNombre}</p>
                            {pago.deportistaCedula && (
                              <p className="text-[11px] text-slate-600">CI: {pago.deportistaCedula}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Monto y Vencimiento */}
                      <div className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-slate-500 text-[11px]">Monto</span>
                          <span className="font-bold text-sm text-slate-900">
                            {formatMoney(pago.monto)}
                          </span>
                        </div>
                        <div className="flex flex-col items-end text-right">
                          <span className="text-slate-500 text-[11px]">Vence</span>
                          <span className="font-medium text-slate-900">{formatDate(pago.fechaVencimiento)}</span>
                        </div>
                      </div>

                      {/* Info confirmación */}
                      <div className="pt-2 border-t border-slate-100 space-y-1">
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">Creado:</span>
                          <span>{formatDate(pago.creadoEn)}</span>
                        </div>
                        {pago.colaboradorNombre && pago.fechaConfirmacion ? (
                          <div className="flex items-start gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                            <svg className="w-3 h-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="font-medium whitespace-nowrap">Confirmado por:</span>
                              <div className="flex flex-col min-w-0">
                                <span className="font-semibold truncate">{pago.colaboradorNombre}</span>
                                <span className="text-[11px] text-emerald-600">{formatDate(pago.fechaConfirmacion)}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">
                            <svg className="w-3 h-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">Pendiente de confirmación</span>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="mt-2 inline-flex items-center justify-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition"
                      >
                        Ver detalle
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* List View (resumida) */}
      {!loading && viewMode === "list" && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Representante
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Deportista
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Producto / Periodo
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Fechas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Confirmado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Detalle
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((pago) => {
                  const statusVariant =
                    STATUS_VARIANTS[pago.estado] || "default";

                  return (
                    <tr
                      key={pago.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-slate-800">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-slate-900 truncate">
                              {pago.representanteNombre}
                            </span>
                            {pago.representanteCedula && (
                              <span className="text-xs text-slate-500">CI: {pago.representanteCedula}</span>
                            )}
                            <span className="text-xs text-slate-500 truncate">
                              {pago.representanteEmail || pago.representanteTelefono || "—"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-.03-.29-.06-.58-.12-.86l-.93.93c-.3.3-.77.3-1.07 0l-.93-.93c-.06.28-.09.57-.12.86V20h3v-.07zM8 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm6 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-2-6.93V4h3v.07c-.03.29-.06.58-.12.86l-.93-.93c-.3-.3-.77-.3-1.07 0l-.93.93c.06-.28.09-.57.12-.86z" />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-slate-900 truncate">{pago.deportistaNombre}</span>
                            {pago.deportistaCedula && (
                              <span className="text-xs text-slate-500">CI: {pago.deportistaCedula}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">{pago.producto}</span>
                          <span className="text-xs text-slate-500">{pago.periodo}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-900 text-right">
                        {formatMoney(pago.monto)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-start gap-1.5">
                            <svg className="w-3.5 h-3.5 text-slate-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div className="flex flex-col">
                              <span className="text-[11px] text-slate-500 font-medium">Creado</span>
                              <span className="text-xs truncate text-slate-900">{formatDate(pago.creadoEn)}</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <svg className="w-3.5 h-3.5 text-amber-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex flex-col">
                              <span className="text-[11px] text-slate-500 font-medium">Vence</span>
                              <span className="text-xs text-slate-900">{formatDate(pago.fechaVencimiento)}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusVariant}>{pago.estado}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {pago.colaboradorNombre && pago.fechaConfirmacion ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-emerald-700">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="font-medium">{pago.colaboradorNombre}</span>
                            </div>
                            <span className="text-xs text-slate-500 ml-5">{formatDate(pago.fechaConfirmacion)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-red-700">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium">Pendiente</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => openDetalle(pago)}
                            className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition"
                          >
                            Ver detalle
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm">
              No se encontraron pagos con los filtros actuales.
            </div>
          )}
        </div>
      )}

      {/* Modal de detalle (aprobar / rechazar) */}
      <ModalPagoDetalle
        open={modalOpen}
        pago={mapPagoToModal(selectedPago)}
        onClose={closeDetalle}
        onUpdated={async () => {
          await loadPagos();
        }}
      />

      {/* Modal para crear orden de pago */}
      <ModalCrearOrdenPago
        open={createOpen}
        onClose={closeCreateModal}
        atletas={atletas}
        repsById={repsById}
        onCreated={async () => {
          await loadPagos();
        }}
      />
    </div>
  );
}
